// D45 — Wizard de création d'intervention, 5 étapes (CDC §5.1.1, P0)
//
// P58-D : les étapes 2→5 étaient des placeholders affichant « implémentation
// détaillée Sprint 2 finalisation ». Elles collectent maintenant réellement
// les données, et l'étape 1 ne force plus l'engin 1 / l'admin.
//
// Étapes (CDC §5.1.1 + §6 schéma DB) :
//   1. Identification  — engin, type, statut, dates, intervenants, résumé
//   2. Indicateurs     — les 6 compteurs de l'engin (engins.*), avant/après
//   3. Actions        — intervention_actions (ordre, action, description, durée)
//   4. Articles       — articles_utilises (article_id, quantité)
//   5. DA (optionnel) — demandes_achat (objet, motif, lignes) + creer_da
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { interventionsApi } from "@/lib/api";
import { enginsApi } from "@/lib/api";
import { articlesApi } from "@/lib/api";
import { API_BASE } from "@/lib/apiBase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useT } from "@/lib/i18n";
import { ht } from "@/lib/ht";
import { useToast } from "@/components/ui/toast";
import type {
  ActionInput,
  ArticleUtiliseInput,
  IndicatorInput,
  StoreInterventionInput,
} from "@/types/intervention";

const STEPS = [
  { id: 1, title: ht("Identification"), fields: ["engin_id", "type", "statut", "dates", "intervenants", "resume"] },
  { id: 2, title: ht("Indicateurs"), fields: ["indicators"] },
  { id: 3, title: ht("Actions"), fields: ["actions"] },
  { id: 4, title: ht("Articles"), fields: ["articles"] },
  { id: 5, title: ht("DA (optionnel)"), fields: ["demande_achat"] },
];

/**
 * P58-D — Les 6 indicateurs suivis par le CDC §6 (table `engins`).
 * Ce sont exactement les compteurs que le module met à jour en fin
 * d'intervention : c'est ce qui alimente le calcul de cycle préventif.
 */
const INDICATORS = [
  { key: "kilometrage", label: ht("Kilométrage (km)"), step: 1 },
  { key: "compteur_horaire", label: ht("Compteur horaire (h)"), step: 1 },
  { key: "huile_moteur", label: ht("Huile moteur (%)"), step: 1 },
  { key: "huile_boite", label: ht("Huile boîte (%)"), step: 1 },
  { key: "eau_radiateur", label: ht("Eau radiateur (%)"), step: 1 },
  { key: "graissage", label: ht("Graissage (%)"), step: 1 },
] as const;

interface UserLite {
  id: number;
  name: string;
  email: string;
}

interface ArticleLite {
  id: number;
  reference: string;
  designation: string;
  unite: string;
  prix_unitaire_fcfa: number;
}

interface DALigne {
  article_id: number | null;
  designation: string;
  quantite: number;
  unite: string;
  prix_unitaire_fcfa: number;
}

export default function InterventionWizard({ mode = "create" }: { mode?: "create" | "edit" }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const t = useT();
  const params = useParams();
  const qc = useQueryClient();
  const editId = mode === "edit" ? Number(params.id) : null;

  // D48 : en mode edit, on chargerait ici l'intervention existante pour pré-remplir.
  void editId;

  const [step, setStep] = useState(1);

  // P58-D : plus de valeurs en dur. L'utilisateur CHOISIT son engin et ses
  // intervenants ; avant, tout partait sur l'engin 1 et l'admin (id 1).
  const [form, setForm] = useState<Partial<StoreInterventionInput>>({
    type: "preventive",
    statut: "planned",
    indicators: [],
    actions: [],
    articles_utilises: [],
    demande_achat: null,
    creer_da: false,
  });
  const [actionDraft, setActionDraft] = useState({ action: "", description: "", duree_min: "" });
  const [articleSearch, setArticleSearch] = useState("");
  const [daLignes, setDaLignes] = useState<DALigne[]>([]);
  // P58-D : objet et motif étaient codés en dur au moment du submit.
  const [daObjet, setDaObjet] = useState("");
  const [daMotif, setDaMotif] = useState("");

  const { data: engins } = useQuery({
    queryKey: ["engins"],
    queryFn: () => enginsApi.list(),
  });
  const { data: articles } = useQuery({
    queryKey: ["articles"],
    queryFn: () => articlesApi.list(),
  });
  const { data: users } = useQuery({
    queryKey: ["users-picker"],
    queryFn: async () => {
      const r = await fetch(`${API_BASE}/v1/users`, { credentials: "include" });
      if (!r.ok) return [] as UserLite[];
      return (await r.json()) as UserLite[];
    },
  });

  // L'engin sélectionné porte les valeurs « avant » des indicateurs.
  const selectedEngin = engins?.find((e) => e.id === form.engin_id);

  const setIndicator = (key: string, side: "before" | "after", raw: string) => {
    const value = raw === "" ? null : Number(raw);
    const current = form.indicators ?? [];
    const idx = current.findIndex((i) => i.key === key);
    const next = idx >= 0 ? current.map((i) => (i.key === key ? { ...i, [side]: value } : i)) : current;
    setForm({ ...form, indicators: next });
  };

  const ensureIndicator = (key: string, before: number | null): IndicatorInput => {
    const current = form.indicators ?? [];
    return current.find((i) => i.key === key) ?? { key, before, after: before ?? 0 };
  };

  const addAction = () => {
    if (!actionDraft.action.trim()) return;
    const next: ActionInput = {
      action: actionDraft.action.trim(),
      description: actionDraft.description.trim() || null,
      duree_min: actionDraft.duree_min === "" ? null : Number(actionDraft.duree_min),
    };
    setForm({ ...form, actions: [...(form.actions ?? []), next] });
    setActionDraft({ action: "", description: "", duree_min: "" });
  };

  const removeAction = (idx: number) => {
    setForm({ ...form, actions: (form.actions ?? []).filter((_, i) => i !== idx) });
  };

  const addArticle = (a: ArticleLite) => {
    const current = form.articles_utilises ?? [];
    if (current.some((x) => x.article_id === a.id)) return;
    const next: ArticleUtiliseInput[] = [...current, { article_id: a.id, quantite: 1 }];
    setForm({ ...form, articles_utilises: next });
    // Alimente aussi la DA si elle est ouverte : c'est le même besoin.
    if (form.creer_da && !daLignes.some((l) => l.article_id === a.id)) {
      setDaLignes([
        ...daLignes,
        {
          article_id: a.id,
          designation: a.designation,
          quantite: 1,
          unite: a.unite,
          prix_unitaire_fcfa: a.prix_unitaire_fcfa ?? 0,
        },
      ]);
    }
    setArticleSearch("");
  };

  const setArticleQty = (articleId: number, quantite: number) => {
    setForm({
      ...form,
      articles_utilises: (form.articles_utilises ?? []).map((x) =>
        x.article_id === articleId ? { ...x, quantite } : x,
      ),
    });
  };

  const removeArticle = (articleId: number) => {
    setForm({
      ...form,
      articles_utilises: (form.articles_utilises ?? []).filter((x) => x.article_id !== articleId),
    });
  };

  const mutation = useMutation({
    mutationFn: (data: StoreInterventionInput) =>
      editId ? interventionsApi.update(editId, data) : interventionsApi.create(data),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["interventions"] });
      navigate(`/maintenance/${result.id}`);
      toast({
        title: editId ? t("wizard.intervention_updated") : t("wizard.intervention_created"),
        description: result.intervention_code,
        variant: 'success',
      });
    },
    onError: (err) => {
      toast({
        title: t("wizard.error_intervention"),
        description: (err as Error).message,
        variant: 'error',
      });
    },
  });

  const submit = () => {
    if (!form.engin_id || !form.intervenant_id || !form.superviseur_id) {
      toast({ title: ht("Champs manquants"), description: ht("Remplir tous les champs obligatoires de l'étape 1"), variant: "warning" });
      setStep(1);
      return;
    }
    const payload: StoreInterventionInput = {
      ...(form as StoreInterventionInput),
      // P58-D : n'envoyer que les indicateurs réellement remplis.
      indicators: (form.indicators ?? []).filter((i) => i.after !== null && i.after !== undefined),
      actions: form.actions ?? [],
      articles_utilises: form.articles_utilises ?? [],
      demande_achat:
        form.creer_da && daLignes.length > 0
          ? {
              objet: daObjet.trim() || t("wizard.da_default_object"),
              motif: daMotif.trim()
                || ht("Remplacement des articles consommés lors de l'intervention"),
              fournisseur_id: null,
              lignes: daLignes,
            }
          : null,
      creer_da: form.creer_da && daLignes.length > 0,
    } as StoreInterventionInput;
    mutation.mutate(payload);
  };

  const filteredArticles = (articles ?? []).filter((a) => {
    const q = articleSearch.trim().toLowerCase();
    if (!q) return false;
    return (
      a.reference.toLowerCase().includes(q) ||
      a.designation.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">
          {editId ? ht("Modifier l'intervention") : ht("Nouvelle intervention")}
        </h1>
      </div>

      {/* Stepper */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-2">
            {STEPS.map((s) => (
              <button
                key={s.id}
                onClick={() => setStep(s.id)}
                className={`flex-1 rounded px-2 py-1.5 text-xs font-medium transition-colors ${
                  step === s.id ? "bg-primary text-primary-foreground" : "bg-muted"
                }`}
              >
                {s.id}. {s.title}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Contenu de l'étape */}
      <Card>
        <CardHeader>
          <CardTitle>{STEPS[step - 1].title}</CardTitle>
        </CardHeader>
        <CardContent>
          {/* ── ÉTAPE 1 — Identification ─────────────────────────────── */}
          {step === 1 && (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm">{ht("Engin")} *</label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.engin_id ?? ""}
                  onChange={(e) => {
                    const id = e.target.value === "" ? undefined : Number(e.target.value);
                    // P58-D : les indicateurs « avant » se pré-remplissent
                    // depuis l'engin sélectionné (CDC : on compare avant/après).
                    const eng = (engins ?? []).find((x) => x.id === id);
                    const base = eng
                      ? INDICATORS.map((ind) => ({
                          key: ind.key,
                          before: (eng as unknown as Record<string, number>)[ind.key] ?? null,
                          after: (eng as unknown as Record<string, number>)[ind.key] ?? 0,
                        }))
                      : [];
                    setForm({ ...form, engin_id: id, indicators: base });
                  }}
                >
                  <option value="">{ht("— Sélectionner un engin —")}</option>
                  {(engins ?? []).map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.code} — {e.immatriculation}
                    </option>
                  ))}
                </select>
                {!form.engin_id && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {ht("L'engin est obligatoire : une intervention concerne toujours une machine précise.")}
                  </p>
                )}
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm">{ht("Type")}</label>
                  <select
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.type}
                    onChange={(e) =>
                      setForm({ ...form, type: e.target.value as "preventive" | "curative" })
                    }
                  >
                    <option value="preventive">{ht("Préventive")}</option>
                    <option value="curative">{ht("Curative")}</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm">{ht("Statut")}</label>
                  <select
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.statut}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        statut: e.target.value as
                          | "planned"
                          | "in_progress"
                          | "completed"
                          | "cancelled",
                      })
                    }
                  >
                    <option value="planned">{ht("Planifiée")}</option>
                    <option value="in_progress">{ht("En cours")}</option>
                    <option value="completed">{ht("Terminée")}</option>
                    <option value="cancelled">{ht("Annulée")}</option>
                  </select>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm">{ht("Date prévue")}</label>
                  <input
                    type="date"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.date_prevue ?? ""}
                    onChange={(e) => setForm({ ...form, date_prevue: e.target.value || null })}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm">{ht("Date début")}</label>
                  <input
                    type="date"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.date_debut ?? ""}
                    onChange={(e) => setForm({ ...form, date_debut: e.target.value || null })}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm">{ht("Date fin")}</label>
                  <input
                    type="date"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.date_fin ?? ""}
                    onChange={(e) => setForm({ ...form, date_fin: e.target.value || null })}
                  />
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm">{ht("Intervenant")} *</label>
                  <select
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.intervenant_id ?? ""}
                    onChange={(e) =>
                      setForm({ ...form, intervenant_id: e.target.value ? Number(e.target.value) : undefined })
                    }
                  >
                    <option value="">{ht("— Sélectionner —")}</option>
                    {(users ?? []).map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm">{ht("Superviseur")} *</label>
                  <select
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.superviseur_id ?? ""}
                    onChange={(e) =>
                      setForm({ ...form, superviseur_id: e.target.value ? Number(e.target.value) : undefined })
                    }
                  >
                    <option value="">{ht("— Sélectionner —")}</option>
                    {(users ?? []).map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm">{ht("Résumé")}</label>
                <textarea
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  rows={3}
                  value={form.resume ?? ""}
                  onChange={(e) => setForm({ ...form, resume: e.target.value })}
                  placeholder={ht("Ex: contrôle trimestriel, vidange + filtres")}
                />
              </div>
            </div>
          )}

          {/* ── ÉTAPE 2 — Indicateurs ──────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {ht("Valeurs relevées sur la machine. La colonne « avant » est pré-remplie depuis l'engin sélectionné ; saisissez la valeur constatée en fin d'intervention.")}
              </p>
              {!form.engin_id && (
                <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
                  {ht("Choisissez d'abord un engin à l'étape 1 pour pré-remplir les valeurs « avant ».")}
                </p>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                      <th className="px-3 py-2">{ht("Indicateur")}</th>
                      <th className="px-3 py-2 w-32">{ht("Avant")}</th>
                      <th className="px-3 py-2 w-32">{ht("Après")}</th>
                      <th className="px-3 py-2 w-24">{ht("Écart")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {INDICATORS.map((ind) => {
                      const cur = ensureIndicator(
                        ind.key,
                        (selectedEngin as unknown as Record<string, number> | undefined)?.[
                          ind.key
                        ] ?? null,
                      );
                      const delta =
                        cur.before !== null && cur.after !== null
                          ? Number((cur.after - cur.before).toFixed(2))
                          : null;
                      return (
                        <tr key={ind.key} className="border-b" data-indicator={ind.key}>
                          <td className="px-3 py-2">{ind.label}</td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              step={ind.step}
                              name={`before-${ind.key}`}
                              data-before={ind.key}
                              className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                              value={cur.before ?? ""}
                              onChange={(e) => setIndicator(ind.key, "before", e.target.value)}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              step={ind.step}
                              name={`after-${ind.key}`}
                              data-after={ind.key}
                              className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                              value={cur.after ?? ""}
                              onChange={(e) => setIndicator(ind.key, "after", e.target.value)}
                            />
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {delta === null ? "—" : delta > 0 ? `+${delta}` : delta}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── ÉTAPE 3 — Actions ─────────────────────────────────── */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {ht("Listez les opérations réellement réalisées, dans l'ordre.")}
              </p>
              <div className="grid gap-2 md:grid-cols-[2fr_3fr_1fr_auto]">
                <input
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder={ht("Opération (ex: vidange moteur)")}
                  value={actionDraft.action}
                  onChange={(e) => setActionDraft({ ...actionDraft, action: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && addAction()}
                />
                <input
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder={ht("Description / observation")}
                  value={actionDraft.description}
                  onChange={(e) => setActionDraft({ ...actionDraft, description: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && addAction()}
                />
                <input
                  type="number"
                  min={0}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder={ht("Durée (min)")}
                  title="duree_min"
                  value={actionDraft.duree_min}
                  onChange={(e) => setActionDraft({ ...actionDraft, duree_min: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && addAction()}
                />
                <Button onClick={addAction} disabled={!actionDraft.action.trim()}>
                  <Plus className="h-3.5 w-3.5" />
                  <span className="ml-1">{ht("Ajouter")}</span>
                </Button>
              </div>

              {(form.actions ?? []).length === 0 ? (
                <p className="rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
                  {ht("Aucune action enregistrée. Ajoutez la première opération ci-dessus.")}
                </p>
              ) : (
                <ol className="space-y-2">
                  {(form.actions ?? []).map((a, i) => (
                    <li
                      key={i}
                      data-testid="action-row"
                      className="flex items-start gap-3 rounded-md border px-3 py-2"
                    >
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{a.action}</p>
                        {a.description && (
                          <p className="text-xs text-muted-foreground">{a.description}</p>
                        )}
                      </div>
                      {a.duree_min != null && (
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {a.duree_min} min
                        </span>
                      )}
                      <button
                        data-testid="action-remove"
                        onClick={() => removeAction(i)}
                        aria-label={ht("Supprimer l'action")}
                        className="shrink-0 rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}

          {/* ── ÉTAPE 4 — Articles ────────────────────────────────── */}
          {step === 4 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {ht("Articles consommés pendant l'intervention. Ils seront déduits du stock.")}
              </p>
              <div className="relative">
                <input
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder={ht("Rechercher un article (référence ou désignation)")}
                  value={articleSearch}
                  onChange={(e) => setArticleSearch(e.target.value)}
                />
                {articleSearch.trim() && filteredArticles.length > 0 && (
                  <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border bg-popover shadow-md">
                    {filteredArticles.slice(0, 20).map((a) => (
                      <li key={a.id}>
                        <button
                          onClick={() => addArticle(a)}
                          className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent"
                        >
                          <span>
                            <span className="font-medium">{a.reference}</span>
                            <span className="ml-2 text-muted-foreground">{a.designation}</span>
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {a.prix_unitaire_fcfa?.toLocaleString("fr-FR")} FCFA / {a.unite}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {(form.articles_utilises ?? []).length === 0 ? (
                <p className="rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
                  {ht("Aucun article consommé. C'est normal pour une intervention sans consommable.")}
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                      <th className="px-3 py-2">{ht("Article")}</th>
                      <th className="px-3 py-2 w-28">{ht("Quantité")}</th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.articles_utilises ?? []).map((u) => {
                      const art = (articles ?? []).find((a) => a.id === u.article_id);
                      return (
                        <tr key={u.article_id} className="border-b">
                          <td className="px-3 py-2">
                            <span className="font-medium">{art?.reference ?? `#${u.article_id}`}</span>
                            <span className="ml-2 text-muted-foreground">{art?.designation ?? ""}</span>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              min={0}
                              step={0.01}
                              className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                              value={u.quantite}
                              onChange={(e) =>
                                setArticleQty(u.article_id, Number(e.target.value))
                              }
                            />
                          </td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => removeArticle(u.article_id)}
                              aria-label={ht("Retirer l'article")}
                              className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ── ÉTAPE 5 — Demande d'achat (optionnel) ────────────── */}
          {step === 5 && (
            <div className="space-y-4">
              <label className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-3">
                <input
                  type="checkbox"
                  checked={form.creer_da ?? false}
                  onChange={(e) => {
                    setForm({ ...form, creer_da: e.target.checked });
                    if (e.target.checked && !daObjet) {
                      setDaObjet(t("wizard.da_default_object"));
                    }
                  }}
                />
                <span className="text-sm">
                  {ht("Créer une demande d'achat pour les consommables à renouveler")}
                </span>
              </label>

              {form.creer_da ? (
                <div className="space-y-3">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm">
                        {ht("Objet de la demande")} *
                      </label>
                      <input
                        name="da_objet"
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={daObjet}
                        onChange={(e) => setDaObjet(e.target.value)}
                        placeholder={ht("Ex: Renouvellement consommables")}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm">
                        {ht("Motif de la demande")} *
                      </label>
                      <input
                        name="da_motif"
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={daMotif}
                        onChange={(e) => setDaMotif(e.target.value)}
                        placeholder={ht("Ex: Niveau de stock bas après intervention")}
                      />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {ht("Ajoutez les articles à commander ci-dessous.")}
                  </p>
                  {daLignes.length === 0 ? (
                    <p className="rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
                      {ht("Aucune ligne. Revenez à l'étape 4 pour ajouter des articles, ou saisissez une ligne ci-dessous.")}
                    </p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                          <th className="px-3 py-2">{ht("Désignation")}</th>
                          <th className="px-3 py-2 w-24">{ht("Qté")}</th>
                          <th className="px-3 py-2 w-24">{ht("Unité")}</th>
                          <th className="px-3 py-2 w-32">{ht("PU (FCFA)")}</th>
                          <th className="w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {daLignes.map((l, i) => (
                          <tr key={i} className="border-b">
                            <td className="px-3 py-2">
                              <input
                                className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                                value={l.designation}
                                onChange={(e) => {
                                  const next = [...daLignes];
                                  next[i] = { ...l, designation: e.target.value };
                                  setDaLignes(next);
                                }}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min={1}
                                className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                                value={l.quantite}
                                onChange={(e) => {
                                  const next = [...daLignes];
                                  next[i] = { ...l, quantite: Number(e.target.value) };
                                  setDaLignes(next);
                                }}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                                value={l.unite}
                                onChange={(e) => {
                                  const next = [...daLignes];
                                  next[i] = { ...l, unite: e.target.value };
                                  setDaLignes(next);
                                }}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min={0}
                                className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                                value={l.prix_unitaire_fcfa}
                                onChange={(e) => {
                                  const next = [...daLignes];
                                  next[i] = { ...l, prix_unitaire_fcfa: Number(e.target.value) };
                                  setDaLignes(next);
                                }}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <button
                                onClick={() => setDaLignes(daLignes.filter((_, x) => x !== i))}
                                aria-label={ht("Retirer la ligne")}
                                className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ) : (
                <p className="rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
                  {ht("Aucune demande d'achat ne sera créée. Cochez la case ci-dessus si des consommables doivent être renouvelés.")}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setStep(Math.max(1, step - 1))}
          disabled={step === 1}
        >{ht("← Étape précédente")}
        </Button>
        {step < STEPS.length ? (
          <Button onClick={() => setStep(step + 1)}>{ht("Étape suivante →")}</Button>
        ) : (
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? ht("Enregistrement...") : ht("Enregistrer")}
          </Button>
        )}
      </div>
    </div>
  );
}